import { CSSProperties } from "react";
import { useTranslation } from "react-i18next";
import Select from "@/components/atoms/Select";
import Text from "@/components/atoms/Text";
import { AddRowButton, HeaderText, PhotoLine, RemoveRowButton, UnitNumberInput } from "./FormParts";
import { InspectionFormApi, InspectionSummary } from "../useInspectionForm";
import { useInspectionFormat } from "../useInspectionFormat";
import { DeviationCosts } from "../useDeviationPreview";
import { InspectionErrors, MAX_DEVIATIONS, MAX_PHOTOS_PER_STREAM } from "../validation";
import styles from "../styles.module.css";

const COLUMNS = "minmax(0, 1fr) minmax(0, 1fr) 130px 110px 36px";

/** The columns are narrow; the options ("Restavfall · kr 850/tonn") get room to stay on one line. */
const WIDE_DROPDOWN = { width: 300, position: "bottom-start" } as const;

interface DeviationProductsTableProps {
  form: InspectionFormApi;
  summary: InspectionSummary;
  errors: InspectionErrors;
  costs: DeviationCosts | null;
}

/**
 * "Avviksvarer": parts of the original order's weight that turned out to be one of the company's deviation
 * products, such as residual waste or gravel. Each row is weighed and photographed; the weight is taken off the
 * order and priced at the product's own price, whatever the contract.
 */
export default function DeviationProductsTable({ form, summary, errors, costs }: DeviationProductsTableProps) {
  const { t } = useTranslation();
  const format = useInspectionFormat();
  const { deviationProducts } = form.values;
  const error = (path: string) => (errors[path] ? t(errors[path]) : undefined);

  const productOptions = summary.deviationProducts.map((product) => ({
    value: String(product.id),
    label: `${product.name} · ${format.unitPrice(product.price, product.currency, format.deviationUnit(product.unitCode), false)}`,
  }));

  return (
    <div className={styles.lineTable} style={{ "--line-columns": COLUMNS } as CSSProperties}>
      <div className={`${styles.lineGrid} ${styles.lineHeader}`}>
        <HeaderText>{t("inspections:deviations.original")}</HeaderText>
        <HeaderText>{t("inspections:deviations.product")}</HeaderText>
        <HeaderText>{t("inspections:deviations.amount")}</HeaderText>
        <HeaderText className={styles.alignRight}>{t("inspections:deviations.cost")}</HeaderText>
        <span />
      </div>

      {deviationProducts.map((row, index) => {
        const path = (field: string) => `deviationProducts.${index}.${field}`;
        const cost = costs?.products[index];
        return (
          <div key={index} className={styles.lineRow} data-testid={`inspection-product-${index}`}>
            <div className={styles.lineGrid}>
              {/* Always the original order's product: the deviation products are taken out of its weight. */}
              <Text size="sm" className={styles.lineWide} data-label={t("inspections:deviations.original")}>
                {summary.productName}
              </Text>
              <Select
                className={styles.lineWide}
                aria-label={t("inspections:deviations.product")}
                placeholder={t("inspections:deviations.selectProduct")}
                data={productOptions}
                comboboxProps={WIDE_DROPDOWN}
                value={row.deviationProductId === null ? null : String(row.deviationProductId)}
                onChange={(value) => form.updateProduct(index, { deviationProductId: value ? Number(value) : null })}
                error={error(path("deviationProductId"))}
                data-testid={`inspection-product-${index}-product`}
              />
              <UnitNumberInput
                aria-label={t("inspections:deviations.amount")}
                unit="kg"
                allowDecimal={false}
                value={row.weightKg}
                onChange={(weightKg) => form.updateProduct(index, { weightKg })}
                error={error(path("weightKg"))}
                data-testid={`inspection-product-${index}-weight`}
              />
              <Text
                size="sm"
                className={styles.alignRight}
                data-label={t("inspections:deviations.cost")}
                data-testid={`inspection-product-${index}-cost`}
              >
                {costs && cost !== undefined ? format.amount(cost, costs.currency) : "—"}
              </Text>
              <RemoveRowButton onClick={() => form.removeProduct(index)} label={t("inspections:deviations.remove")} />
            </div>
            <PhotoLine
              photos={row.photos}
              onChange={(photos) => form.updateProduct(index, { photos })}
              max={MAX_PHOTOS_PER_STREAM}
              error={error(path("photos"))}
              data-testid={`inspection-product-${index}-photos`}
            />
          </div>
        );
      })}

      <div className={styles.lineFooter}>
        <div className={styles.lineFooterStart}>
          <AddRowButton
            onClick={form.addProduct}
            disabled={!summary.canAddProducts || summary.deviationRowCount >= MAX_DEVIATIONS}
            data-testid="inspection-add-product"
          >
            {t("inspections:deviations.addProduct")}
          </AddRowButton>
          {!summary.canAddProducts && (
            <Text size="xs" color="tertiary" data-testid="inspection-no-products">
              {t("inspections:deviations.noProducts")}
            </Text>
          )}
        </div>
        {costs && deviationProducts.length > 0 && (
          <Text size="sm" data-testid="inspection-products-sum">
            {t("inspections:deviations.productsSum", { amount: format.amount(costs.productsTotal, costs.currency) })}
          </Text>
        )}
      </div>
    </div>
  );
}
